import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { PackageTag } from '@/components/package-tag';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useBookingSearch,
  type BookingSearchModel,
} from '@/features/manage-bookings/bookings.api';
import {
  BookingAmount,
  PaymentCategoryCell,
} from '@/features/manage-bookings/payment-category';
import { BOOKING_STATUS } from '@/features/manage-bookings/types';
import { formatDateTime, formatPhone } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * The bookings behind one tile on the overview.
 *
 * A drawer rather than a jump to the management grid: opening a tile is a
 * question about that number, and answering it should not cost the reader the
 * page they were reading. The grid is still one click away at the bottom, for
 * when the answer turns into work.
 *
 * The rows come from the same `AdminBookingSearchModel` the tile was computed
 * from, with only the tile's own filter patched over it, so the list can never
 * disagree with the figure that opened it.
 */

const PAGE_SIZE = 20;

export function BookingRowsSheet({
  model,
  patch,
  title,
  open,
  onOpenChange,
  onViewBooking,
}: {
  model: BookingSearchModel;
  patch: Partial<BookingSearchModel> | null;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewBooking: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [patch]);

  const query = useBookingSearch(
    { ...model, ...patch, page, size: PAGE_SIZE },
    open && patch !== null
  );

  const rows = query.data?.bookings ?? [];
  const total = query.data?.total ?? 0;
  // Paging keeps the last page on screen while the next one loads.
  const busy = query.isFetching && !query.isPending;

  const gridHref = () => {
    const params = new URLSearchParams();
    if (patch?.status !== undefined && patch.status !== null) {
      params.set('status', String(patch.status));
    }
    if (
      patch?.paymentCategory !== undefined &&
      patch.paymentCategory !== null
    ) {
      params.set('paymentCategory', String(patch.paymentCategory));
    }
    if (model.locationId) params.set('locationId', model.locationId);
    if (model.serviceTypeId) params.set('serviceTypeId', model.serviceTypeId);
    if (model.hostId) params.set('hostId', model.hostId);
    if (model.eventType !== null) {
      params.set('eventType', String(model.eventType));
    }
    return `/booking/manage/bookings?${params}`;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className="space-y-3 p-4">
          <p className="text-muted-foreground text-sm">
            {query.isSuccess
              ? t('sales.showingOf', { shown: rows.length, total })
              : ' '}
          </p>

          {/* `isLoading`: the query is off while the drawer is closed. */}
          {query.isLoading && (
            <div className="divide-y rounded-xl border">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="space-y-2 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1.5">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-52" />
                    </div>
                    <Skeleton className="h-5 w-16" />
                  </div>
                  <Skeleton className="h-3 w-48" />
                </div>
              ))}
            </div>
          )}

          {query.isError && (
            <p className="text-muted-foreground py-16 text-center text-sm">
              {t('sales.loadFailed')}
            </p>
          )}

          {query.isSuccess && rows.length === 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={busy} />
              <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
                {t('sales.noRows')}
              </p>
            </div>
          )}

          {rows.length > 0 && (
            <>
              <div className="relative rounded-xl">
                <BusyOverlay show={busy} />
                <div
                  className={cn(
                    'divide-y rounded-xl border',
                    busy && 'opacity-60 transition-opacity'
                  )}
                >
                  {rows.map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => onViewBooking(row.id)}
                      className="hover:bg-accent/40 w-full space-y-2 p-3 text-left transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {[row.firstName, row.lastName]
                              .filter(Boolean)
                              .join(' ')}
                          </p>
                          <p className="text-muted-foreground truncate text-xs">
                            {row.email}
                          </p>
                          {row.phoneNumber && (
                            <p className="text-muted-foreground truncate text-xs tabular-nums">
                              {formatPhone(row.phoneNumber)}
                            </p>
                          )}
                        </div>
                        <div className="shrink-0 text-right text-sm">
                          <BookingAmount row={row} />
                        </div>
                      </div>

                      <div>
                        <p className="text-sm">{row.eventName}</p>
                        <p className="text-muted-foreground text-xs">
                          {row.locationName} ·{' '}
                          {formatDateTime(row.timeslotStartTime)}
                        </p>
                        <PackageTag
                          nameEn={row.packageNameEn}
                          nameEs={row.packageNameEs}
                          className="mt-1.5"
                        />
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5">
                        {row.status === BOOKING_STATUS.confirmed && (
                          <Badge variant="success">{t('sales.confirmed')}</Badge>
                        )}
                        {row.status === BOOKING_STATUS.pending && (
                          <Badge variant="warning">{t('sales.pending')}</Badge>
                        )}
                        {row.status === BOOKING_STATUS.cancelled && (
                          <Badge variant="destructive">
                            {t('sales.cancelled')}
                          </Badge>
                        )}
                        {row.checkedInAt && (
                          <Badge variant="success">{t('sales.checkedIn')}</Badge>
                        )}
                        <PaymentCategoryCell row={row} language={language} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <Pagination
                page={page}
                size={PAGE_SIZE}
                total={total}
                onPageChange={setPage}
              />
            </>
          )}

          <Button variant="outline" className="w-full" asChild>
            <Link to={gridHref()}>
              <ExternalLink />
              {t('sales.openInBookings')}
            </Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
